import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { PaperProps } from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import { snakeCase } from 'lodash';
import { ReactElement, ReactNode, useRef } from 'react';
import isScrollable from '../helpers/isScrollable';
import DialogHeader from './DialogHeader';

type NoCloseReason = 'escapeKeyDown' | 'backdropClick';
type HeaderVariant = 'primary' | 'danger';

interface IModalProps extends Omit<DialogProps, 'onClose' | 'open' | 'title' | 'PaperProps'> {
  actions?: ReactNode;
  canClose?: boolean;
  children?: ReactNode;
  headerVariant?: HeaderVariant;
  name: string;
  noCloseReasons?: NoCloseReason[];
  onClose: () => void;
  open?: boolean;
  PaperProps?: Partial<PaperProps>;
  title?: string | ReactElement;
}

const styles = {
  actions: {
    '& button': {
      ml: '10px',
    },
    pb: 3,
    pt: 2,
    px: 3,
  },
  actionsBorderTop: {
    '&.MuiDialogActions-root': {
      borderTop: '1px solid',
      borderTopColor: (theme: { palette: { common: { black: string } } }) => alpha(theme.palette.common.black, 0.12),
    },
  },
  content: {
    '&.MuiDialogContent-root': {
      pb: 3,
      pt: 2.25,
      px: 3,
    },
  },
};

const Modal = ({
  actions,
  canClose,
  children,
  headerVariant,
  name,
  noCloseReasons = [],
  onClose,
  open = false,
  PaperProps,
  title,
  ...rest
}: IModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const scrollableState = isScrollable(
    dialogContentRef?.current?.getBoundingClientRect()?.height,
    contentRef?.current?.getBoundingClientRect()?.height
  );

  const getHtmlId = (section: string) => `${snakeCase(name)}-${snakeCase(section)}`;

  const onReallyClose = () => {
    if (canClose !== false) {
      onClose();
    }
  };

  return (
    <Dialog
      aria-describedby="alert-dialog-slide-description"
      aria-labelledby="alert-dialog-slide-title"
      fullWidth
      keepMounted
      maxWidth="sm"
      onClose={(_, reason: NoCloseReason) => {
        if (!noCloseReasons.includes(reason)) {
          onReallyClose();
        }
      }}
      open={open}
      slotProps={{ paper: { component: 'div', ...PaperProps } }}
      scroll="paper"
      {...rest}
    >
      {title && (
        <DialogHeader
          canClose={canClose}
          headerVariant={headerVariant}
          id={getHtmlId('title')}
          onCancel={onClose}
          title={title}
        />
      )}
      <DialogContent id={getHtmlId('content')} ref={dialogContentRef} sx={styles.content}>
        <div ref={contentRef}>{children}</div>
      </DialogContent>
      {actions && (
        <DialogActions
          id={getHtmlId('actions')}
          sx={{
            ...styles.actions,
            ...(scrollableState && styles.actionsBorderTop),
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
